import { useEffect, useState } from 'react';
import { Mail, MessageCircle, FileText, HardDrive, CheckCircle2, XCircle, Plus, Trash2, Copy, RefreshCw } from 'lucide-react';
import { get, put, post, API_BASE } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import FormBuilder from '../../components/FormBuilder';
import { Card, Button, Field, Input, Select, Textarea, Badge, Tabs, Loader, SectionTitle } from '../../components/UI';
import { fmtDateTime } from '../../utils/format';

/**
 * Platform settings (super admin): how email and WhatsApp are sent, and the
 * forms every new event starts with.
 */
export default function Settings() {
  const toast = useToast();
  const [tab, setTab] = useState('email');
  const [data, setData] = useState(null);

  const load = async () => {
    try { setData(await get('/settings')); } catch (err) { toast.error(err.message); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <Loader full label="Loading settings" />;

  return (
    <>
      <SectionTitle title="Settings" subtitle="Messaging connections and the forms every event starts with" />
      <Tabs active={tab} onChange={setTab} tabs={[
        { key: 'email', label: 'Email', icon: Mail },
        { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
        { key: 'storage', label: 'Uploads', icon: HardDrive },
        { key: 'forms', label: 'Default forms', icon: FileText },
      ]} />
      {tab === 'email' && <EmailSettings data={data.email} onSaved={setData} />}
      {tab === 'whatsapp' && <WhatsAppSettings data={data.whatsapp} onSaved={setData} />}
      {tab === 'storage' && <StorageSettings data={data.storage} onSaved={setData} />}
      {tab === 'forms' && <DefaultForms forms={data.defaultForms} onSaved={setData} />}
    </>
  );
}

function Status({ active, source, verifiedAt, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
      {active
        ? <Badge tone="success"><CheckCircle2 size={11} /> {label} is on{source === 'env' ? ' (from server .env)' : ''}</Badge>
        : <Badge tone="warning"><XCircle size={11} /> {label} is off — messages are only logged</Badge>}
      {verifiedAt && <span style={{ fontSize: 12, color: 'var(--text-lo)' }}>Last checked {fmtDateTime(verifiedAt)}</span>}
    </div>
  );
}

function EmailSettings({ data, onSaved }) {
  const toast = useToast();
  const [f, setF] = useState({ host: data.host || '', port: data.port || 587, secure: Boolean(data.secure), user: data.user || '', pass: '', from: data.from || '', enabled: Boolean(data.enabled) });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState('');

  const save = async (enabled = f.enabled) => {
    setSaving(true);
    try { const res = await put('/settings/email', { ...f, enabled }); onSaved(res.data); setF({ ...f, pass: '', enabled }); toast.success(res.message); }
    catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };
  const test = async () => {
    setTesting(true);
    try { const res = await post('/settings/email/test', testTo ? { to: testTo } : {}); toast.success(res.message); onSaved(await get('/settings')); }
    catch (err) { toast.error(err.message); }
    finally { setTesting(false); }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, alignItems: 'start' }}>
      <Card padding={22}>
        <Status active={data.active} source={data.source} verifiedAt={data.verifiedAt} label="Email" />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px', gap: 12 }}>
          <Field label="SMTP host" required><Input value={f.host} onChange={(e) => setF({ ...f, host: e.target.value })} placeholder="smtp.gmail.com" /></Field>
          <Field label="Port"><Input type="number" value={f.port} onChange={(e) => setF({ ...f, port: e.target.value })} /></Field>
        </div>
        <Field label="Username" required><Input value={f.user} onChange={(e) => setF({ ...f, user: e.target.value })} placeholder="events@yourcompany.com" /></Field>
        <Field label="Password / app password" hint={data.hasPass ? 'A password is saved — leave blank to keep it' : undefined}>
          <Input type="password" value={f.pass} onChange={(e) => setF({ ...f, pass: e.target.value })} autoComplete="new-password" />
        </Field>
        <Field label="Send as" hint='Shown to recipients, e.g. "Golden Lotus <events@yourcompany.com>"'>
          <Input value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} />
        </Field>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-mid)', cursor: 'pointer', marginBottom: 14 }}>
          <input type="checkbox" checked={f.secure} onChange={(e) => setF({ ...f, secure: e.target.checked })} style={{ accentColor: 'var(--gold-400)' }} />
          Use SSL (port 465). Leave off for STARTTLS on 587.
        </label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button onClick={() => save()} loading={saving}>Save</Button>
          {f.enabled
            ? <Button variant="subtle" onClick={() => save(false)} loading={saving}>Turn off</Button>
            : <Button variant="outline" onClick={() => save(true)} loading={saving}>Save and turn on</Button>}
        </div>
      </Card>

      <Card padding={22}>
        <p style={{ fontSize: 15, fontWeight: 600 }}>Test the connection</p>
        <p style={{ fontSize: 12.5, color: 'var(--text-lo)', marginTop: 4, lineHeight: 1.6 }}>
          Checks the login with the saved settings and sends one test email. Save first.
        </p>
        <Field label="Send the test to"><Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="Your address (defaults to your login email)" /></Field>
        <Button variant="outline" icon={Mail} onClick={test} loading={testing} disabled={!data.active}>Send test email</Button>
        <p style={{ fontSize: 12, color: 'var(--text-lo)', marginTop: 16, lineHeight: 1.7 }}>
          Gmail and Google Workspace need an <strong>app password</strong> (Google account → Security → 2-step verification → App passwords), not the normal password.
          For Outlook / Microsoft 365 use smtp.office365.com on port 587.
        </p>
      </Card>
    </div>
  );
}

function WhatsAppSettings({ data, onSaved }) {
  const toast = useToast();
  const [f, setF] = useState({ token: '', phoneNumberId: data.phoneNumberId || '', businessAccountId: data.businessAccountId || '', apiVersion: data.apiVersion || 'v21.0', verifyToken: data.verifyToken || '', appSecret: '', enabled: Boolean(data.enabled) });
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const apiHost = API_BASE.replace(/\/api\/?$/, '');
  const webhookUrl = `${apiHost}${data.webhookPath}`;

  const save = async (enabled = f.enabled) => {
    setSaving(true);
    try { const res = await put('/settings/whatsapp', { ...f, enabled }); onSaved(res.data); setF({ ...f, token: '', appSecret: '', enabled }); toast.success(res.message); }
    catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };
  const check = async () => {
    setChecking(true);
    try { const res = await post('/settings/whatsapp/verify', {}); onSaved(res.data); toast.success(res.message); }
    catch (err) { toast.error(err.message); }
    finally { setChecking(false); }
  };
  const copy = (t) => navigator.clipboard?.writeText(t).then(() => toast.success('Copied'));

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, alignItems: 'start' }}>
      <Card padding={22}>
        <Status active={data.active} source={data.source} verifiedAt={data.verifiedAt} label="WhatsApp" />
        {data.displayNumber && (
          <div style={{ padding: '10px 12px', background: 'var(--ink-900)', border: '1px solid var(--border)', borderRadius: 'var(--r)', marginBottom: 14, fontSize: 13 }}>
            <p><strong>{data.verifiedName || 'Business number'}</strong> · +{data.displayNumber}</p>
            {data.qualityRating && <p style={{ fontSize: 12, color: 'var(--text-lo)', marginTop: 3 }}>Quality rating: {data.qualityRating}</p>}
          </div>
        )}
        <Field label="Permanent access token" required hint={data.hasToken ? 'A token is saved — leave blank to keep it' : 'From Meta Business → System users → Generate token (whatsapp_business_messaging, whatsapp_business_management)'}>
          <Input type="password" value={f.token} onChange={(e) => setF({ ...f, token: e.target.value })} autoComplete="off" />
        </Field>
        <Field label="Phone number id" required><Input value={f.phoneNumberId} onChange={(e) => setF({ ...f, phoneNumberId: e.target.value })} placeholder="1065…" /></Field>
        <Field label="WhatsApp Business Account id" hint="Needed to create and check templates"><Input value={f.businessAccountId} onChange={(e) => setF({ ...f, businessAccountId: e.target.value })} placeholder="1234…" /></Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="API version"><Input value={f.apiVersion} onChange={(e) => setF({ ...f, apiVersion: e.target.value })} /></Field>
          <Field label="App secret" hint={data.hasAppSecret ? 'Saved' : 'Optional — verifies webhook signatures'}><Input type="password" value={f.appSecret} onChange={(e) => setF({ ...f, appSecret: e.target.value })} autoComplete="off" /></Field>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button onClick={() => save()} loading={saving}>Save</Button>
          {f.enabled
            ? <Button variant="subtle" onClick={() => save(false)} loading={saving}>Turn off</Button>
            : <Button variant="outline" onClick={() => save(true)} loading={saving}>Save and turn on</Button>}
          <Button variant="outline" icon={RefreshCw} onClick={check} loading={checking} disabled={!data.active}>Check connection</Button>
        </div>
      </Card>

      <Card padding={22}>
        <p style={{ fontSize: 15, fontWeight: 600 }}>Receive replies (webhook)</p>
        <p style={{ fontSize: 12.5, color: 'var(--text-lo)', marginTop: 4, lineHeight: 1.6 }}>
          So replies and delivery ticks reach the Messages inbox, register this URL in Meta → your app → WhatsApp → Configuration → Webhook, and subscribe to <strong>messages</strong>.
        </p>
        <Field label="Callback URL">
          <div style={{ display: 'flex', gap: 6 }}>
            <Input value={webhookUrl} readOnly style={{ flex: 1 }} />
            <Button variant="outline" size="sm" icon={Copy} onClick={() => copy(webhookUrl)} />
          </div>
        </Field>
        <Field label="Verify token" hint="Any secret phrase — enter the same one in Meta">
          <div style={{ display: 'flex', gap: 6 }}>
            <Input value={f.verifyToken} onChange={(e) => setF({ ...f, verifyToken: e.target.value })} style={{ flex: 1 }} />
            <Button variant="outline" size="sm" onClick={() => setF({ ...f, verifyToken: Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2) })}>Generate</Button>
          </div>
        </Field>
        <Button variant="outline" onClick={() => save()} loading={saving}>Save verify token</Button>
        <p style={{ fontSize: 12, color: 'var(--text-lo)', marginTop: 16, lineHeight: 1.7 }}>
          WhatsApp only lets a business start a conversation with an <strong>approved template</strong>. Create templates on the Messages page;
          free-text replies work for 24 hours after a person writes to you.
        </p>
      </Card>
    </div>
  );
}

function StorageSettings({ data, onSaved }) {
  const toast = useToast();
  const [f, setF] = useState({
    bucket: data.bucket || '', region: data.region || 'ap-south-1', prefix: data.prefix ?? 'goldenmice',
    accessKeyId: data.accessKeyId || '', secretAccessKey: '', publicUrl: data.publicUrl || '', enabled: data.enabled !== false,
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const save = async (enabled = f.enabled) => {
    setSaving(true);
    try { const res = await put('/settings/storage', { ...f, enabled }); onSaved(res.data); setF({ ...f, secretAccessKey: '', enabled }); toast.success(res.message); }
    catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };
  const test = async () => {
    setTesting(true);
    try { const res = await post('/settings/storage/test', {}); toast.success(res.message); onSaved(await get('/settings')); }
    catch (err) { toast.error(err.message); }
    finally { setTesting(false); }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, alignItems: 'start' }}>
      <Card padding={22}>
        <Status active={data.active} source={data.source} verifiedAt={data.verifiedAt} label="File storage" />
        {!data.active && (
          <p style={{ fontSize: 12.5, color: 'var(--warn)', marginBottom: 14, lineHeight: 1.6 }}>
            Until this is set, attachments, tickets, photos and logos cannot be saved — uploads fail with "the file could not be stored".
          </p>
        )}
        <Field label="Bucket name" required><Input value={f.bucket} onChange={(e) => setF({ ...f, bucket: e.target.value })} placeholder="my-event-files" /></Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Region" required><Input value={f.region} onChange={(e) => setF({ ...f, region: e.target.value })} placeholder="ap-south-1" /></Field>
          <Field label="Folder inside the bucket" hint="Keeps this platform's files together"><Input value={f.prefix} onChange={(e) => setF({ ...f, prefix: e.target.value })} placeholder="goldenmice" /></Field>
        </div>
        <Field label="Access key id" required><Input value={f.accessKeyId} onChange={(e) => setF({ ...f, accessKeyId: e.target.value })} autoComplete="off" /></Field>
        <Field label="Secret access key" required={!data.hasSecret} hint={data.hasSecret ? 'A secret is saved — leave blank to keep it' : undefined}>
          <Input type="password" value={f.secretAccessKey} onChange={(e) => setF({ ...f, secretAccessKey: e.target.value })} autoComplete="new-password" />
        </Field>
        <Field label="Public address" hint="Only if a CDN or custom domain serves the bucket, e.g. https://files.example.com">
          <Input value={f.publicUrl} onChange={(e) => setF({ ...f, publicUrl: e.target.value })} />
        </Field>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button onClick={() => save()} loading={saving}>Save</Button>
          {f.enabled
            ? <Button variant="subtle" onClick={() => save(false)} loading={saving}>Turn off</Button>
            : <Button variant="outline" onClick={() => save(true)} loading={saving}>Save and turn on</Button>}
          <Button variant="outline" icon={HardDrive} onClick={test} loading={testing} disabled={!data.active}>Test an upload</Button>
        </div>
      </Card>

      <Card padding={22}>
        <p style={{ fontSize: 15, fontWeight: 600 }}>Where your files go</p>
        {data.live ? (
          <p style={{ fontSize: 13, color: 'var(--text-mid)', marginTop: 8, lineHeight: 1.7 }}>
            Uploads are saved to <strong style={{ color: 'var(--text-hi)' }}>{data.live.bucket}</strong> ({data.live.region})
            {data.live.prefix ? <> under <strong style={{ color: 'var(--text-hi)' }}>{data.live.prefix}/</strong></> : null}.
            {data.source === 'env' ? ' These values come from the server\'s environment; anything saved here replaces them.' : ''}
          </p>
        ) : (
          <p style={{ fontSize: 13, color: 'var(--text-mid)', marginTop: 8, lineHeight: 1.7 }}>Nothing is set yet.</p>
        )}
        <p style={{ fontSize: 12, color: 'var(--text-lo)', marginTop: 16, lineHeight: 1.7 }}>
          <strong>Test an upload</strong> writes a small file, opens it and deletes it again — it reports exactly what is wrong if something is
          (a wrong key, a bucket in another region, or files not being publicly readable).
        </p>
        <p style={{ fontSize: 12, color: 'var(--text-lo)', marginTop: 12, lineHeight: 1.7 }}>
          The keys need permission to put, read and delete objects in this bucket. Files are stored with unguessable names.
        </p>
      </Card>
    </div>
  );
}

const BLANK_FORM = {
  title: '', description: '', placement: 'app_home', loginIdentifier: 'mobile', publish: true, fields: [],
  attendance: { identifier: 'employeeCode', requireCompanions: false, maxCompanions: 0, allowWalkIns: false },
};

function DefaultForms({ forms: initial, onSaved }) {
  const toast = useToast();
  const [forms, setForms] = useState(initial.map((f) => ({ ...f, fields: f.fields || [] })));
  const [open, setOpen] = useState(initial.length ? 0 : null);
  const [saving, setSaving] = useState(false);

  const update = (i, patch) => setForms(forms.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const remove = (i) => { setForms(forms.filter((_, j) => j !== i)); setOpen(null); };
  const add = () => { setForms([...forms, { ...BLANK_FORM }]); setOpen(forms.length); };

  const save = async () => {
    for (const f of forms) {
      if (!f.title.trim()) return toast.error('Every form needs a title');
      if (!f.fields.length) return toast.error(`"${f.title}" has no questions yet`);
    }
    setSaving(true);
    try { const res = await put('/settings/default-forms', { forms }); onSaved(res.data); toast.success(res.message); }
    catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <>
      <Card padding={18} style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <p style={{ fontSize: 14.5, fontWeight: 600 }}>Forms every new event starts with</p>
            <p style={{ fontSize: 12.5, color: 'var(--text-lo)', marginTop: 3, lineHeight: 1.6 }}>
              When an event is created these are added to it automatically — an enquiry card on the attendee home screen,
              a registration form to share, or both. Existing events are not changed.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="outline" icon={Plus} onClick={add}>Add a form</Button>
            <Button onClick={save} loading={saving}>Save</Button>
          </div>
        </div>
      </Card>

      {!forms.length && <p style={{ fontSize: 13, color: 'var(--text-lo)', padding: 20, textAlign: 'center' }}>No default forms yet. Add one to start every event with the same questions.</p>}

      {forms.map((f, i) => (
        <Card key={i} padding={18} style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', cursor: 'pointer' }} onClick={() => setOpen(open === i ? null : i)}>
            <div style={{ minWidth: 0 }}>
              <p style={{ fontSize: 14.5, fontWeight: 600 }}>{f.title || 'Untitled form'}</p>
              <p style={{ fontSize: 12, color: 'var(--text-lo)', marginTop: 2 }}>
                {f.placement === 'registration' ? 'Registration link'
                  : f.placement === 'attendance' ? 'Asked at check-in'
                  : 'Home screen card'} · {f.fields.length} question{f.fields.length === 1 ? '' : 's'} · {f.publish ? 'published on creation' : 'created as a draft'}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <Button variant="subtle" size="sm" icon={Trash2} onClick={(e) => { e.stopPropagation(); remove(i); }} />
              <Badge tone="neutral">{open === i ? 'Hide' : 'Edit'}</Badge>
            </div>
          </div>

          {open === i && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                <Field label="Title" required><Input value={f.title} onChange={(e) => update(i, { title: e.target.value })} placeholder="Arrival details" /></Field>
                <Field label="Where it appears">
                  <Select value={f.placement} onChange={(e) => update(i, { placement: e.target.value })}>
                    <option value="app_home">Attendee home screen (enquiry card)</option>
                    <option value="attendance">Asked at check-in (counts the party)</option>
                    <option value="registration">Registration link (public)</option>
                  </Select>
                </Field>
                {f.placement === 'attendance' && (
                  <>
                    <Field label="What identifies them at the gate?" hint="Must also be a required question mapped to that value">
                      <Select value={f.attendance?.identifier || 'employeeCode'}
                        onChange={(e) => update(i, { attendance: { ...f.attendance, identifier: e.target.value } })}>
                        <option value="employeeCode">Employee id</option>
                        <option value="mobile">Mobile number</option>
                        <option value="email">Email address</option>
                      </Select>
                    </Field>
                    <Field label="Most people they may bring" hint="0 for no limit">
                      <Input type="number" min="0" value={f.attendance?.maxCompanions ?? 0}
                        onChange={(e) => update(i, { attendance: { ...f.attendance, maxCompanions: Number(e.target.value) || 0 } })} />
                    </Field>
                  </>
                )}
                {f.placement === 'registration' && (
                  <Field label="People sign in with">
                    <Select value={f.loginIdentifier} onChange={(e) => update(i, { loginIdentifier: e.target.value })}>
                      <option value="mobile">Mobile number (OTP)</option>
                      <option value="email">Email + generated password</option>
                    </Select>
                  </Field>
                )}
              </div>
              <Field label="Description"><Textarea rows={2} value={f.description || ''} onChange={(e) => update(i, { description: e.target.value })} placeholder="Shown above the questions" /></Field>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-mid)', cursor: 'pointer', marginBottom: 14 }}>
                <input type="checkbox" checked={f.publish} onChange={(e) => update(i, { publish: e.target.checked })} style={{ accentColor: 'var(--gold-400)' }} />
                Publish as soon as the event is created
              </label>
              {f.placement === 'attendance' && (
                <>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-mid)', cursor: 'pointer', marginBottom: 10 }}>
                    <input type="checkbox" checked={Boolean(f.attendance?.requireCompanions)} style={{ accentColor: 'var(--gold-400)' }}
                      onChange={(e) => update(i, { attendance: { ...f.attendance, requireCompanions: e.target.checked } })} />
                    They must say how many people are with them before they can check in
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-mid)', cursor: 'pointer', marginBottom: 14 }}>
                    <input type="checkbox" checked={Boolean(f.attendance?.allowWalkIns)} style={{ accentColor: 'var(--gold-400)' }}
                      onChange={(e) => update(i, { attendance: { ...f.attendance, allowWalkIns: e.target.checked } })} />
                    Somebody not on the uploaded sheet may add themselves (needs a question mapped to "Name")
                  </label>
                </>
              )}
              {f.placement === 'attendance' && (
                <p style={{ fontSize: 12, color: 'var(--text-lo)', marginBottom: 12, lineHeight: 1.7 }}>
                  A check-in form needs a required question mapped to the detail above, and one mapped to
                  <strong> People along with them</strong> — that is what counts the party against the uploaded sheet.
                </p>
              )}
              <p style={{ fontSize: 11.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--text-lo)', marginBottom: 8 }}>Questions</p>
              <FormBuilder fields={f.fields} onChange={(fields) => update(i, { fields })} />
            </div>
          )}
        </Card>
      ))}
    </>
  );
}
